import { Divider } from "rsuite";
import PropTypes from "prop-types";
import React from "react";
import _ from "lodash";
import { DateTime } from "luxon";
import { createUseStyles } from "react-jss";
import jssVariables from "../../jss_setup";
import { countries } from "../../umbilical_tools";
import { InputField } from "../forms/inputGroup";
import { DatepickerField, SelectField } from "../forms/inputGroup/helpers";
import { default_guest_nationality } from "../../actions/single_search/accommodation_search";
import { useSelector } from "react-redux";

function getRoomOptionLabel({ idx, room, assignedGuests }) {
  const adt = room.adults;
  const chd = room.children;
  const adtLabel = adt > 1 ? `${adt} Adults` : `${adt} Adult`;
  const chdLabel =
    chd > 1 ? `, ${chd} Children` : chd === 0 ? "" : `, ${chd} Child`;
  const roomName = room.name;

  const guestLabel = assignedGuests
    .map((g) => (g.first_name ? `${g.first_name} ${g.last_name}` : ""))
    .join(", ");

  return `${idx + 1}. ${roomName} (${adtLabel}${chdLabel}) ${guestLabel}`;
}

const styles = createUseStyles({
  TravellerForm: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gridGap: jssVariables.normal_gap,
    padding: jssVariables.normal_gap,
    border: (props) => `2px solid ${jssVariables.colors.base_color}`,
    borderRadius: "5px",
  },
  header: {
    gridColumn: "span 2",
    color: "black",
    "& small": { color: "black" },
  },
  divider: {
    margin: `calc(${jssVariables.normal_gap} / 2) 0`,
    gridColumn: "span 2",
  },
  span2: { gridColumn: "span 2" },
});
const TravellerForm = ({
  idx,
  type,
  traveller,
  travellers,
  requiredFields,
  roomData,
}) => {
  const classes = styles();

  const {
    searchData: { children_ages },
  } = useSelector((state) => state.HotelSearch);
  const age = _.get(children_ages, idx, 12);

  return (
    <div className={classes.TravellerForm}>
      <h5 className={classes.header}>
        {type === "ADT" ? "Adult" : "Child"} {idx + 1}
        {type === "CHD" && <small>{` (${age} years old at check out)`}</small>}
        {traveller.group_leader ? " (Lead Guest)" : ""}
      </h5>
      <Divider className={classes.divider} />
      {requiredFields.title && (
        <SelectField
          name={`adults.${idx}.guest_title`}
          label="Title"
          options={[
            ["MR", "Mr."],
            ["MS", "Ms."],
            ["MRS", "Mrs."],
            ["MISS", "Miss."],
            ["DR", "Dr."],
          ]}
        />
      )}
      <div />
      {requiredFields.first_name && (
        <InputField name={`adults.${idx}.first_name`} label="First Name" />
      )}
      {requiredFields.last_name && (
        <InputField name={`adults.${idx}.last_name`} label="Last Name" />
      )}
      {requiredFields.gender && (
        <SelectField
          name={`adults.${idx}.gender`}
          label="Gender"
          placeholder={[null, "------"]}
          options={[
            ["MA", "Male"],
            ["FE", "Female"],
          ]}
        />
      )}
      {requiredFields.email && (
        <InputField name={`adults.${idx}.email`} label="Email" />
      )}
      {requiredFields.phone && (
        <InputField name={`adults.${idx}.phone`} label="Telephone" />
      )}
      {requiredFields.date_of_birth && (
        <DatepickerField
          name={`adults.${idx}.date_of_birth`}
          label="Date of Birth"
          yearDropdownItemsNum={90}
          showYearDropdown={true}
          maxDate={DateTime.now()
            .minus({ year: type === "ADT" ? 12 : 0 })
            .toISO()}
        />
      )}
      {requiredFields.nationality && (
        <SelectField
          name={`adults.${idx}.nationality`}
          label="Nationality"
          options={_.sortBy(
            _.sortBy(Object.entries(countries), (v) => v[1]),
            (v) => v[0] !== default_guest_nationality
          ).map(([code, name]) => [code, `${name} (${code})`])}
        />
      )}
      {requiredFields.identification_code && (
        <InputField
          name={`adults.${idx}.identification_code`}
          label="Passport Number"
        />
      )}
      {requiredFields.identification_expiry_date && (
        <DatepickerField
          name={`adults.${idx}.identification_expiry_date`}
          label="Passport Expiration Date"
          yearDropdownItemsNum={15}
          showYearDropdown={true}
          minDate={DateTime.now().plus({ months: 1 }).toISO()}
        />
      )}
      {roomData.length > 0 && (
        <div className={classes.span2}>
          <SelectField
            name={`adults.${idx}.room_idx_uid`}
            label="Room"
            customStyles={{ select: { width: "5rem" } }}
            options={roomData
              .filter((room) => {
                // If this guest is assigned to this room show the option
                if (room.room_idx_uid === traveller.room_idx_uid) {
                  return true;
                }

                // For all other guests hide the room option if it is already full.
                const assignedGuests = travellers.filter(
                  (g) => g.room_idx_uid === room.room_idx_uid
                ).length;
                if (assignedGuests < room.adults + room.children) {
                  return true;
                }
                return false;
              })
              .map((room, idx) => {
                const assignedGuests = travellers.filter(
                  (g) => g.room_idx_uid === room.room_idx_uid
                );

                return [
                  room.room_idx_uid,
                  getRoomOptionLabel({ idx, room, assignedGuests }),
                ];
              })}
          />
        </div>
      )}
    </div>
  );
};
TravellerForm.defaultProps = {
  requiredFields: {},
};
TravellerForm.propTypes = {
  requiredFields: PropTypes.object,
  idx: PropTypes.number.isRequired,
  type: PropTypes.oneOf(["ADT", "CHD"]).isRequired,
  traveller: PropTypes.object.isRequired,
  travellers: PropTypes.array.isRequired,
  roomData: PropTypes.array.isRequired,
};

export default TravellerForm;
